import { useState } from 'react';
import { Alert, Box, IconButton, InputAdornment, Paper, TextField, Typography, useTheme } from '@mui/material';
import {
  AlternateEmail as AlternateEmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { IsEmail, IsNotEmpty, IsString } from 'class-validator';
import { useForm } from 'react-hook-form';
import { Outlet, useNavigate } from 'react-router-dom';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useAuthenticate } from '../hooks/api/use-authentication.hook';
import { CustomTheme } from '../contexts/theme.context';
import { LoginLayout } from '../layouts/LoginLayout';
import { useSession } from '../contexts/session.context';

class LoginForm {
  @IsEmail()
  email: string;

  @IsString()
  @IsNotEmpty()
  password: string;
}

export const Login = () => {
  const theme = useTheme<CustomTheme>();
  const navigate = useNavigate();
  const [sessionError, setSessionError] = useSession();
  const [authenticate, { loading, error: authenticateError }] = useAuthenticate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: classValidatorResolver(LoginForm) });

  const [showPassword, setShowPassword] = useState(false);

  const error = authenticateError?.response?.data?.message || sessionError;

  return (
    <LoginLayout>
      <Outlet />
      <Paper elevation={6} sx={{ ml: '2.5rem', mr: '2.5rem' }}>
        <form
          onSubmit={handleSubmit(async (values) => {
            setSessionError(undefined);
            await authenticate(values);
            navigate(0);
          })}
        >
          <Box display="flex" sx={{ flexDirection: 'column', p: '1.25rem' }}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Box flexShrink={0} sx={{ pr: 2 }}>
                <img src={theme.companyLogo} style={{ height: '100px' }} />
              </Box>
              <Box alignItems="center">
                <Typography variant="h4" align="center">
                  {theme.companyName}
                </Typography>
              </Box>
            </Box>

            <TextField
              {...register('email')}
              variant="outlined"
              style={{ marginTop: '1rem' }}
              disabled={loading}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              margin="dense"
              size="small"
              type="email"
              label="Email"
              tabIndex={1}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled size="small">
                        <AlternateEmailIcon style={{ color: '#777' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              {...register('password')}
              variant="outlined"
              style={{ marginTop: '1rem' }}
              disabled={loading}
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              margin="dense"
              size="small"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              tabIndex={2}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ color: '#777' }}
                        tabIndex={-1}
                        size="small"
                      >
                        {!showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={loading}
              style={{ margin: '4px 0px' }}
              startIcon={<VpnKeyIcon />}
            >
              Login
            </LoadingButton>

            <Box style={{ marginTop: '.5rem' }}>
              <a
                href="#"
                style={{ fontSize: '0.9rem', color: '#777', textDecoration: 'none' }}
                onClick={() => navigate('recover-password')}
              >
                Forgot my password.
              </a>
            </Box>
            {error ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {error}
              </Alert>
            ) : null}
          </Box>
        </form>
      </Paper>
    </LoginLayout>
  );
};
